// The time-saved ledger: what the CRM did that someone would otherwise have
// done by hand, priced in minutes. Counted from what actually happened, never
// guessed; agency admins can change the minutes so the total stays believable.
// Minutes follow published timings where there are any (timesheet keying 4-6
// minutes, onboarding paperwork 45 minutes) and Bullhorn's 2 minutes per step.
import type { Way } from "./ways.ts";

export const LEDGER = {
	hours_by_link: {
		label: "Hours sent in by the candidate's link",
		minutes: 5,
		instead: "typing in a paper or emailed timesheet",
	},
	approved_by_link: {
		label: "Timesheets approved by the client's link",
		minutes: 4,
		instead: "emailing or phoning the client for approval",
	},
	reminders: {
		label: "Reminders sent for you",
		minutes: 3,
		instead: "chasing hours, approvals and documents by phone",
		way: "timesheet_reminders",
	},
	offers_filled: {
		label: "Shifts filled by an open offer",
		minutes: 15,
		instead: "ringing round five or more people",
		way: "auto_offer",
	},
	offers_answered: {
		label: "Offers answered by link",
		minutes: 4,
		instead: "a call to ask and a note of the answer",
	},
	registrations: {
		label: "People who registered themselves",
		minutes: 40,
		instead: "keying in a registration from paper",
	},
	forms_back: {
		label: "Forms filled in by candidates",
		minutes: 10,
		instead: "typing in bank, health or emergency details",
	},
	documents_by_link: {
		label: "Documents added by candidates",
		minutes: 5,
		instead: "collecting, scanning and filing a copy",
	},
	dates_read: {
		label: "Expiry dates read from photos",
		minutes: 2,
		instead: "reading and typing the date",
	},
	rtw_checks: {
		label: "Right to work checks done with GOV.UK for you",
		minutes: 6,
		instead: "typing each share code into GOV.UK, saving the PDF and filing it",
		way: "auto_rtw_check",
	},
	client_requests: {
		label: "Bookings the client made themselves",
		minutes: 8,
		instead: "taking the order by phone and keying it in",
	},
	repeats: {
		label: "Bookings repeated for next week",
		minutes: 6,
		instead: "keying next week's booking by hand",
		way: "auto_repeat",
	},
	notices: {
		label: "People told about changes and welcomes",
		minutes: 3,
		instead: "a call or text to each person",
		way: "shift_change_email",
	},
	payroll_files: {
		label: "Payroll files made for you",
		minutes: 15,
		instead: "building and checking the payroll file",
		way: "auto_payroll",
	},
	shortlist_answers: {
		label: "Shortlist answers from clients by link",
		minutes: 6,
		instead: "chasing the client for feedback",
	},
} as const satisfies Record<string, { label: string; minutes: number; instead: string; way?: Way }>;

export type LedgerKey = keyof typeof LEDGER;
export const LEDGER_KEYS = Object.keys(LEDGER) as LedgerKey[];

/** The agency's own minutes on top of the defaults. */
export function ledgerMinutes(stored: unknown): Record<LedgerKey, number> {
	const saved = stored && typeof stored === "object" ? (stored as Record<string, unknown>) : {};
	return Object.fromEntries(
		LEDGER_KEYS.map((k) => {
			const v = saved[k];
			return [k, typeof v === "number" && v >= 0 && v <= 240 ? v : LEDGER[k].minutes];
		}),
	) as Record<LedgerKey, number>;
}

/** "3 h 20 min", "45 min", "0 min". */
export function minutesText(total: number) {
	const m = Math.round(total);
	const h = Math.floor(m / 60);
	const r = m % 60;
	if (!h) return `${r} min`;
	return r ? `${h} h ${r} min` : `${h} h`;
}
